const MAX_ZOOM_IN_LEVELS = 2;
const MAX_ZOOM_OUT_LEVELS = 3;

/**
 * Pure computation: given an accumulated wheel delta, compute how many zoom levels
 * to change and in which direction, using Leaflet's sigmoid formula.
 */
export function computeZoomDelta(
  accumulatedDelta: number,
  wheelPxPerZoomLevel: number,
  zoomInMultiplier: number,
  zoomOutMultiplier: number,
): { zoomLevels: number; direction: 'in' | 'out' | 'none' } {
  if (accumulatedDelta === 0) {
    return { zoomLevels: 0, direction: 'none' };
  }

  // Normalize: positive delta = zoom in, negative = zoom out
  const direction: 'in' | 'out' = accumulatedDelta > 0 ? 'in' : 'out';

  // Step 1: Convert pixel delta to a continuous zoom value
  // Leaflet divides by (4 * wheelPxPerZoomLevel) to normalize scroll speed
  const d2 = accumulatedDelta / (4 * wheelPxPerZoomLevel);

  // Step 2: Apply Leaflet's sigmoid formula to cap extreme scrolls
  // This maps large scroll deltas into a bounded ~4 zoom level range
  const d3 = 4 * Math.log(2 / (1 + Math.exp(-Math.abs(d2)))) / Math.LN2;

  // Step 3: Apply asymmetric multiplier based on direction
  const multiplier = direction === 'in' ? zoomInMultiplier : zoomOutMultiplier;
  const d3Adjusted = d3 * multiplier;

  // Step 4: Snap to integer zoom levels (at least 1 if there was any delta)
  const rounded = Math.max(1, Math.round(d3Adjusted));

  // Step 5: Cap per-gesture zoom to prevent excessive jumps
  const maxLevels = direction === 'in' ? MAX_ZOOM_IN_LEVELS : MAX_ZOOM_OUT_LEVELS;
  const zoomLevels = Math.min(rounded, maxLevels);

  return { zoomLevels, direction };
}
